import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import FolderIcon from '@mui/icons-material/Folder';
import { tokens } from '../../../global/theme/tokens';
import DateLabel from '../../label/DateLabel';
import { formatBytes } from '../explorer/MediaInfo';
import {
  entryKind,
  entryLabel,
  FOLDER_COLOR,
  folderPurposes,
  listingToEntries,
  sortEntriesByKind,
} from './explorerUtils';

const GRID_COLUMNS = 'minmax(200px, 1fr) 90px 150px 130px';

const ExplorerListView = ({
  listing,
  query,
  sortBy,
  sortOrder,
  onSortChange,
  selected,
  onSelect,
  onOpen,
  onContextMenu,
  renaming,
  onRenamingChange,
  onRenameCommit,
  onRenameCancel,
  newFolderName,
  onNewFolderChange,
  onNewFolderCommit,
  onNewFolderCancel,
  dnd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  let entries = listingToEntries(listing);
  if (sortBy === 'kind') entries = sortEntriesByKind(entries, sortOrder);
  const trimmedQuery = query.trim().toLowerCase();
  if (trimmedQuery)
    entries = entries.filter((entry) =>
      entryLabel(entry).toLowerCase().includes(trimmedQuery),
    );

  const headers = [
    { key: 'name', label: t('Name') },
    { key: 'size', label: t('Size'), align: 'right' },
    { key: 'kind', label: t('Kind') },
    { key: 'lastModified', label: t('Modified') },
  ];

  const SortIcon = sortOrder === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box className="flex-1 min-h-0 flex flex-col overflow-x-auto">
      <Box sx={{ minWidth: '620px' }} className="flex flex-col flex-1 min-h-0">
        {/* Header */}
        <Box
          className="px-3 py-1"
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLUMNS,
            gap: '8px',
            borderBottom: `1px solid ${colors.glass}`,
          }}
        >
          {headers.map((header) => (
            <Box
              key={header.key}
              onClick={() => onSortChange(header.key)}
              className="flex flex-row items-center gap-1 cursor-pointer select-none"
              sx={{ justifyContent: header.align === 'right' ? 'flex-end' : 'flex-start' }}
            >
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {header.label}
              </Typography>
              {sortBy === header.key && (
                <SortIcon sx={{ fontSize: '14px', opacity: 0.6 }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Rows */}
        <Box className="flex-1 min-h-0 overflow-y-auto px-2 py-1">
          {newFolderName !== null && (
            <Box
              className="flex flex-row items-center gap-2 px-2 py-1 rounded-lg"
              sx={{ backgroundColor: colors.bgInput }}
            >
              <FolderIcon sx={{ fontSize: '20px', color: FOLDER_COLOR }} />
              <TextField
                size="small"
                autoFocus
                value={newFolderName}
                placeholder={t('New Folder')}
                onChange={(event) => onNewFolderChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onNewFolderCommit();
                  if (event.key === 'Escape') onNewFolderCancel();
                }}
                onBlur={onNewFolderCommit}
              />
            </Box>
          )}

          {entries.map((entry) => {
            const kind = entryKind(entry);
            const purposes = folderPurposes(entry);
            const isSelected = selected?.path === entry.path;
            const isDragTarget =
              dnd.dragOverPath === entry.path && entry.isDir;
            const isRenaming = renaming?.entry?.path === entry.path;
            return (
              <Box
                key={entry.path}
                draggable={dnd.canEdit && !isRenaming}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(entry);
                }}
                onDoubleClick={() => onOpen(entry)}
                onContextMenu={(event) => onContextMenu(event, entry)}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move';
                  dnd.start(entry.path);
                }}
                onDragEnd={dnd.end}
                onDragOver={(event) => {
                  if (!entry.isDir) return;
                  if (dnd.dragPath && dnd.dragPath !== entry.path) {
                    event.preventDefault();
                    event.stopPropagation();
                    dnd.over(entry.path);
                  } else if (!dnd.dragPath && dnd.isFileDrag(event)) {
                    event.preventDefault();
                    event.stopPropagation();
                    dnd.fileOver(entry.path);
                  }
                }}
                onDragLeave={() => dnd.leave(entry.path)}
                onDrop={(event) => {
                  if (!entry.isDir) return;
                  if (dnd.dragPath) {
                    event.preventDefault();
                    event.stopPropagation();
                    dnd.dropOn(entry.path);
                  } else if (event.dataTransfer?.files?.length) {
                    event.preventDefault();
                    event.stopPropagation();
                    dnd.fileDrop(entry.path, event.dataTransfer.files);
                  }
                }}
                className="px-2 rounded-lg cursor-default"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  gap: '8px',
                  alignItems: 'center',
                  height: '38px',
                  backgroundColor: isDragTarget
                    ? colors.info + '30'
                    : isSelected
                      ? colors.info + '30'
                      : 'transparent',
                  outline: isDragTarget ? `2px solid ${colors.info}` : 'none',
                  ':hover': {
                    backgroundColor: isSelected
                      ? colors.info + '40'
                      : colors.bgInput,
                  },
                }}
              >
                <Box className="flex flex-row items-center gap-2 min-w-0">
                  <kind.Icon sx={{ fontSize: '20px', color: kind.color }} />
                  {isRenaming ? (
                    <TextField
                      size="small"
                      autoFocus
                      value={renaming.value}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => onRenamingChange(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onRenameCommit();
                        if (event.key === 'Escape') onRenameCancel();
                      }}
                      onBlur={onRenameCommit}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      className="truncate"
                      sx={{ fontWeight: entry.isDir ? 500 : 400 }}
                    >
                      {entryLabel(entry)}
                    </Typography>
                  )}
                  {purposes.map((purpose) => (
                    <Typography
                      key={purpose.key}
                      variant="caption"
                      className="px-2 rounded-full whitespace-nowrap flex flex-row items-center gap-1"
                      sx={{
                        backgroundColor: colors.bgInput,
                        border: `1px solid ${colors.glass}`,
                      }}
                    >
                      <purpose.Icon sx={{ fontSize: '12px' }} />
                      {t(purpose.label)}
                    </Typography>
                  ))}
                  {entry.meta?.workPackage && (
                    <Typography
                      variant="caption"
                      className="px-2 rounded-full whitespace-nowrap"
                      sx={{
                        backgroundColor: colors.bgInput,
                        border: `1px solid ${colors.glass}`,
                      }}
                    >
                      {entry.meta.workPackage.title}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  className="monospace text-right"
                  sx={{ opacity: 0.7 }}
                >
                  {entry.isDir ? '—' : formatBytes(entry.size || 0)}
                </Typography>
                <Typography variant="caption" className="truncate" sx={{ opacity: 0.7 }}>
                  {t(kind.label)}
                </Typography>
                <Typography
                  variant="caption"
                  className="monospace"
                  sx={{ opacity: 0.7 }}
                >
                  {entry.lastModified ? (
                    <DateLabel date={entry.lastModified * 1000} />
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
            );
          })}

          {entries.length === 0 && newFolderName === null && (
            <Box className="flex flex-col items-center justify-center gap-2 py-16 opacity-60">
              <FolderOffIcon sx={{ fontSize: '34px' }} />
              <Typography variant="body2">
                {trimmedQuery
                  ? t('No matching files')
                  : t('No files yet. Upload files to get started')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExplorerListView;
